<!--
  Softphone-Dialog (Twilio Voice, nur ausgehend). Wird aus der
  Termin-Verwaltung bei einem Rückrufwunsch geöffnet und ruft die
  Kundennummer direkt an.

  Wählablauf (Reihenfolge ist wichtig):
    1. /admin/phone/call legt die Tracking-Row an -> call_id
    2. device.connect({params:{To, callId}}) -> Twilio ruft unseren
       TwiML-Webhook auf und wählt die Nummer
    3. ringing  -> CallSid sichern (/admin/phone/sid)
    4. accept   -> /admin/phone/connected
    5. disconnect -> /admin/phone/ended, ~4 s später /admin/phone/poll
       (erst der Poll holt Endstatus + Dauer von Twilio)
-->
<template>
  <div class="phone-overlay" @click.self="close">
    <div class="phone-card">
      <header class="phone-head">
        <strong>📞 Anruf</strong>
        <button class="phone-x" @click="close">✕</button>
      </header>

      <div v-if="contactName" class="phone-contact">{{ contactName }}</div>

      <div v-if="allowed === false" class="phone-note phone-note--warn">
        Für diesen Zugang ist das Telefon nicht freigeschaltet.
        <span v-if="errorMsg">({{ errorMsg }})</span>
      </div>

      <template v-else>
        <input
          v-model="number"
          class="phone-num"
          type="tel"
          inputmode="tel"
          placeholder="Nummer (0…, 00…, +…)"
          :disabled="!['idle', 'ended', 'error'].includes(state)"
        />

        <div class="phone-status" :class="'is-' + state">{{ statusText }}</div>
        <div v-if="callerId" class="phone-cid">von {{ callerId }}</div>

        <div class="phone-actions">
          <button
            v-if="state === 'idle'"
            class="btn btn--primary phone-call"
            :disabled="!ready || !number"
            @click="startCall"
          >
            ▶ Anrufen
          </button>
          <template v-else-if="state === 'ended' || state === 'error'">
            <button
              class="btn btn--primary phone-call"
              :disabled="!number"
              @click="redial"
            >
              ↻ Erneut anrufen
            </button>
            <button class="btn phone-close2" @click="close">Schließen</button>
          </template>
          <button
            v-else
            class="btn btn--danger phone-hang"
            @click="hangup"
          >
            ■ Auflegen
          </button>
        </div>

        <div v-if="errorMsg" class="phone-note phone-note--err">{{ errorMsg }}</div>
      </template>
    </div>
  </div>
</template>

<script>
import { markRaw } from "vue";
import BaseCalls from "@/services/BaseCalls.js";

export default {
  name: "PhoneDialog",
  props: {
    phone: { type: String, default: "" },
    bookingId: { type: Number, default: 0 },
    contactName: { type: String, default: "" },
  },
  emits: ["close"],
  data() {
    return {
      number: this.phone || "",
      allowed: null, // null = unbekannt, true/false
      ready: false, // Device initialisiert
      callerId: "",
      state: "idle", // idle | connecting | ringing | in-call | ended | error
      errorMsg: "",
      device: null,
      call: null,
      callId: 0,
      wakeLock: null,
      pollTimer: null,
    };
  },
  computed: {
    statusText() {
      return (
        {
          idle: this.ready ? "bereit" : "verbinde…",
          connecting: "wähle…",
          ringing: "klingelt…",
          "in-call": "verbunden",
          ended: "Gespräch beendet",
          error: "Fehler",
        }[this.state] || ""
      );
    },
  },
  async mounted() {
    await this.setup();
    document.addEventListener("visibilitychange", this.onVisibility);
  },
  beforeUnmount() {
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.cleanup();
  },
  methods: {
    async setup() {
      try {
        const st = (await BaseCalls.phoneStatus())?.data || {};
        this.allowed = !!st.allowed;
        this.callerId = st.caller_id || "";
        if (!st.allowed) {
          this.errorMsg = st.error || "";
          return;
        }
        const tok = (await BaseCalls.phoneToken())?.data || {};
        if (!tok.success) {
          this.allowed = false;
          this.errorMsg = tok.error || "Token fehlgeschlagen";
          return;
        }
        // @twilio/voice-sdk ist groß -> lazy laden.
        const { Device } = await import("@twilio/voice-sdk");
        // markRaw: Vues Reactive-Proxy verträgt sich nicht mit den
        // read-only-Properties des Twilio-SDK ("property '_log' is
        // read-only … on the proxy target") — Device/Call nie proxien.
        this.device = markRaw(new Device(tok.token, {
          codecPreferences: ["opus", "pcmu"],
        }));
        this.device.on("error", (e) => {
          this.errorMsg = "Twilio: " + (e?.message || e?.code || "Fehler");
          this.state = "error";
        });
        this.ready = true;
      } catch (e) {
        this.allowed = false;
        this.errorMsg = e?.response?.data?.error || e?.message || "Setup-Fehler";
      }
    },

    /** Nach Trennung/Fehler mit einem Klick erneut wählen (neue Tracking-Row,
     *  Device wird wiederverwendet — bei Device-Fehler vorher neu aufgebaut). */
    async redial() {
      if (this.pollTimer) clearTimeout(this.pollTimer);
      this.errorMsg = "";
      this.callId = 0;
      this.call = null;
      this.state = "idle";
      if (!this.ready || !this.device) {
        await this.setup();
        if (!this.ready) return; // setup zeigt die Fehlermeldung
      }
      this.startCall();
    },

    async startCall() {
      if (!this.ready || !this.number) return;
      this.errorMsg = "";
      this.state = "connecting";
      try {
        const r = (await BaseCalls.phoneCall({
          to_number: this.number,
          booking_id: this.bookingId || 0,
        }))?.data || {};
        if (!r.success) {
          this.state = "idle";
          this.errorMsg =
            r.error === "bad_number"
              ? "Ungültige Nummer."
              : r.error === "no_phone"
              ? "Nicht freigeschaltet."
              : r.error || "Anruf konnte nicht gestartet werden.";
          return;
        }
        this.callId = r.call_id;

        this.call = markRaw(await this.device.connect({
          params: { To: r.to_number || this.number, callId: String(r.call_id) },
        }));
        this.bindCall(this.call);
      } catch (e) {
        this.state = "error";
        this.errorMsg = e?.message || "Verbindungsfehler";
      }
    },

    bindCall(call) {
      call.on("ringing", () => {
        this.state = "ringing";
        const sid = call.parameters?.CallSid || "";
        if (sid) BaseCalls.phoneSid({ call_id: this.callId, twilio_call_sid: sid });
      });
      call.on("accept", () => {
        this.state = "in-call";
        const sid = call.parameters?.CallSid || "";
        BaseCalls.phoneConnected({ call_id: this.callId, twilio_call_sid: sid });
        this.acquireWakeLock();
      });
      call.on("disconnect", () => this.afterDisconnect());
      call.on("cancel", () => this.afterDisconnect());
      call.on("error", (e) => {
        this.errorMsg = "Twilio: " + (e?.message || e?.code || "Fehler");
        this.state = "error";
        this.afterDisconnect();
      });
    },

    hangup() {
      try {
        this.call?.disconnect();
      } catch (e) {
        // ignore
      }
    },

    afterDisconnect() {
      if (this.state === "ended") return;
      this.state = "ended";
      this.releaseWakeLock();
      if (this.callId) {
        BaseCalls.phoneEnded({ call_id: this.callId });
        this.pollTimer = setTimeout(() => {
          BaseCalls.phonePoll({ call_id: this.callId }).catch(() => {});
        }, 4000);
      }
    },

    async acquireWakeLock() {
      try {
        if ("wakeLock" in navigator) {
          this.wakeLock = await navigator.wakeLock.request("screen");
        }
      } catch (e) {
        // Wake Lock ist optional
      }
    },
    releaseWakeLock() {
      try {
        this.wakeLock?.release();
      } catch (e) {
        // ignore
      }
      this.wakeLock = null;
    },
    onVisibility() {
      if (document.visibilityState === "visible" && this.state === "in-call") {
        this.acquireWakeLock();
      }
    },

    cleanup() {
      if (this.pollTimer) clearTimeout(this.pollTimer);
      this.releaseWakeLock();
      try {
        this.call?.disconnect();
      } catch (e) {
        // ignore
      }
      try {
        this.device?.destroy();
      } catch (e) {
        // ignore
      }
    },

    close() {
      this.cleanup();
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.phone-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.phone-card {
  background: #fff;
  width: min(360px, 92vw);
  border-radius: 12px;
  padding: 18px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
}
.phone-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.phone-x {
  border: none;
  background: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
}
.phone-contact {
  font-size: 1.05em;
  font-weight: 600;
  margin-bottom: 10px;
}
.phone-num {
  width: 100%;
  padding: 12px 14px;
  font-size: 1.2em;
  border: 1px solid #ccd;
  border-radius: 8px;
  text-align: center;
  letter-spacing: 1px;
  box-sizing: border-box;
}
.phone-num:disabled {
  background: #f4f6fb;
  color: #333;
}
.phone-status {
  text-align: center;
  margin: 12px 0 2px;
  font-weight: 600;
  color: #475569;
}
.phone-status.is-ringing {
  color: #b45309;
}
.phone-status.is-in-call {
  color: #15803d;
}
.phone-status.is-error {
  color: #b91c1c;
}
.phone-cid {
  text-align: center;
  font-size: 0.8em;
  color: #94a3b8;
  margin-bottom: 8px;
}
.phone-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
}
.phone-call,
.phone-hang {
  min-width: 160px;
  padding: 12px 0;
  font-size: 1.05em;
}
.phone-note {
  margin-top: 12px;
  padding: 10px;
  border-radius: 6px;
  font-size: 0.9em;
}
.phone-note--warn {
  background: #fff8e8;
  border-left: 4px solid #f59e0b;
}
.phone-note--err {
  background: #fdecea;
  color: #b91c1c;
}
</style>
